import { getSeoUrl, normalizeCommerceLink } from 'utils/links';
import { getRelativeURLWithParams } from 'utils/url';
import { HEADLESS_SEARCH_PAGE, SEARCH_ROUTE } from 'constants/links';
import { useEsiteCtx } from 'services/esite/context';
import { useState } from 'react';
import { useSearchSuggestionQuery } from 'services/searchSuggestions/use-search-suggestions-query';
import { debounce } from '@cat-ecom/common/utils/commonUtils';
import { DEBOUNCE_TIME_MILLISECONDS } from 'constants/common';
import { useParams, useRouter } from 'next/navigation';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { getPDPUrl } from 'components/product-comparison-grid/product-comparison-card/utils';
import { getSuggestedDiagrams, getSuggestedKeywords } from './utils';

const useSearchSuggestions = ({
  isHeadlessSearchEnabled,
  isHeadlessPDPEnabled,
  model,
  serialNumber
}: {
  isHeadlessSearchEnabled?: boolean;
  isHeadlessPDPEnabled?: boolean;
  model?: string;
  serialNumber?: string;
} = {}) => {
  const { catalogId, langId, storeId, locale, esiteIdentifier } = useEsiteCtx();
  const [searchValue, setSearchValue] = useState('');
  const { data, isPending, isFetching } = useSearchSuggestionQuery({
    searchTerm: searchValue,
    storeIdentifier: esiteIdentifier,
    storeId,
    catalogId,
    langId,
    locale,
    serialNumber
  });
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer).
  const params = useParams();
  const esiteUrl = getSeoUrl(params);
  const suggestions = {
    suggestedKeyWords:
      getSuggestedKeywords(data?.suggestedKeyWords, model, serialNumber) ?? [],
    suggestedProducts: data?.suggestedProducts ?? [],
    suggestedCategories: data?.suggestedCategories ?? [],
    suggestedDiagrams: getSuggestedDiagrams(data?.suggestedDiagrams) ?? []
  };

  const getSuggestions = debounce((searchTerm: string) => {
    setSearchValue(searchTerm);
  }, DEBOUNCE_TIME_MILLISECONDS);

  const { push } = useRouter();
  const onSearch = (searchTerm: string) => {
    if (searchTerm) {
      const searchURL = normalizeCommerceLink(
        getRelativeURLWithParams(SEARCH_ROUTE, {
          catalogId,
          langId,
          searchTerm,
          storeId
        })
      );

      if (suggestions.suggestedProducts.length === 1) {
        const [{ value = '', partNumber }] = suggestions.suggestedProducts;

        const pdpURL = getPDPUrl({
          esiteParams: params,
          isHeadlessPDPEnabled,
          productNumber: partNumber,
          productURL: value
        });

        if (isHeadlessPDPEnabled) {
          push(pdpURL);
        } else {
          window.location.href = pdpURL;
        }
      } else if (isHeadlessSearchEnabled) {
        const stringifiedParams = new URLSearchParams({
          page: '1',
          ...(model && { model, filterByEquipment: 'true' }),
          ...(serialNumber && { serialNumber })
        });
        const basePath = replaceTokensInString(
          HEADLESS_SEARCH_PAGE,
          searchTerm
        );

        window.location.href = `${esiteUrl}${basePath}&${stringifiedParams.toString()}`;
      } else {
        // Workaround for fix this issue AB#1145163
        window.location.href = searchURL;
      }
    }
  };

  const hideResults = isFetching || isPending;

  return { onSearch, getSuggestions, suggestions, hideResults, searchValue };
};

export default useSearchSuggestions;
